/**
 * <vehicle-parts-home> — fills the homepage with parts for the garage's active
 * machine (and a brand page's, when the machine is that make: data-only-make).
 *
 * The vehicle lives in localStorage, so Liquid can't know it when the homepage
 * renders. This fetches the vehicle-parts section against that machine's
 * collection URL via the Section Rendering API, which scopes `collection`
 * correctly server-side — real Liquid-rendered cards with live prices, and no
 * backend involved.
 */
class VehiclePartsHome extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;

    this.slotEl = this.querySelector('[data-parts-slot]');
    this.emptyEl = this.querySelector('[data-parts-empty]');
    this.renderedKey = null;

    this.onGarageChange = () => this.update();
    document.addEventListener('garage:changed', this.onGarageChange);

    this.update();
  }

  disconnectedCallback() {
    document.removeEventListener('garage:changed', this.onGarageChange);
    this.controller?.abort();
  }

  async update() {
    let active = window.Garage?.active();
    // On a brand page the block is that make's alone (data-only-make): a
    // machine of another make is treated as none (2026-10-13).
    const onlyMake = (this.dataset.onlyMake || '').trim().toLowerCase();
    if (onlyMake && active && String(active.make || '').toLowerCase() !== onlyMake) active = null;

    if (!active) {
      this.controller?.abort();
      this.pendingKey = null;
      this.renderedKey = null;
      this.slotEl.replaceChildren();
      this.toggle(false);
      return;
    }

    // Already fetching this machine — let it finish.
    if (active.key === this.pendingKey) return;

    // Already showing it, with nothing in flight that could overwrite it.
    if (active.key === this.renderedKey && !this.pendingKey) return;

    // Supersede any in-flight request. Checking renderedKey alone isn't enough:
    // switching A -> B -> A quickly leaves renderedKey at A while B is still in
    // flight, so an early return here would let B's slow response land on top.
    this.controller?.abort();
    this.controller = new AbortController();
    this.pendingKey = active.key;

    this.setAttribute('data-loading', '');

    try {
      const res = await fetch(
        // sort_by is explicit: these collections default to best-selling, but a
        // merchant changing one collection's default shouldn't silently change
        // what "top sellers" means here.
        `/collections/${encodeURIComponent(active.key)}?sort_by=best-selling&sections=vehicle-parts`,
        { signal: this.controller.signal }
      );
      if (!res.ok) throw new Error(`sections ${res.status}`);

      const data = await res.json();
      const html = data['vehicle-parts'];
      if (!html) throw new Error('section missing from response');

      // The section arrives as trusted first-party Liquid output.
      this.slotEl.innerHTML = html;
      this.renderedKey = active.key;
      this.pendingKey = null;
      this.toggle(true);
    } catch (err) {
      // An aborted request was deliberately superseded — the newer one owns
      // pendingKey now, so leave it alone.
      if (err.name === 'AbortError') return;

      this.pendingKey = null;
      console.error('[vehicle-parts]', err);
      // Leave whatever was already on screen rather than blanking the page.
      if (!this.renderedKey) this.toggle(false);
    } finally {
      this.removeAttribute('data-loading');
    }
  }

  toggle(hasParts) {
    if (this.emptyEl) this.emptyEl.hidden = hasParts;
  }
}

if (!customElements.get('vehicle-parts-home')) {
  customElements.define('vehicle-parts-home', VehiclePartsHome);
}
